import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Star, MessageSquare, FileText, Clock, Image, 
  Settings, MapPin, Phone, Globe, Link2, ChevronRight,
  Sparkles, Send, RefreshCw, CheckCircle, AlertCircle,
  Building, Calendar, Tag, List, Search, Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function GoogleBusinessPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ total: 0, average: 0, unanswered: 0 });
  const [posts, setPosts] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    // Check if just connected
    if (searchParams.get('connected') === 'true') {
      toast.success('Connexion à Google Business réussie !');
    }
    if (searchParams.get('error')) {
      toast.error(`Erreur: ${searchParams.get('error')}`);
    }
    checkConnectionStatus();
  }, [searchParams]);

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/google/status?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      if (response.data.connected) {
        loadBusinessData();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setLoading(false);
    }
  };

  const loadBusinessData = async () => {
    try {
      // Load business info
      const infoResponse = await axios.get(`${BACKEND_URL}/api/google/business-info?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessInfo(infoResponse.data);

      // Load reviews
      const reviewsResponse = await axios.get(`${BACKEND_URL}/api/google/reviews?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(reviewsResponse.data.reviews || []);
      setReviewsStats({
        total: reviewsResponse.data.total_reviews,
        average: reviewsResponse.data.average_rating,
        unanswered: reviewsResponse.data.unanswered_count
      });

      // Load posts
      const postsResponse = await axios.get(`${BACKEND_URL}/api/google/posts?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(postsResponse.data.posts || []);

    } catch (error) {
      console.error('Error loading business data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/google/auth?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Erreur lors de la connexion');
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm('Voulez-vous vraiment déconnecter Google Business ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/google/disconnect?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(false);
      toast.success('Déconnecté de Google Business');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const generateAIReply = async (review) => {
    setGeneratingAI(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/google/reviews/generate-ai-reply`, {
        review_text: review.comment || '',
        reviewer_name: review.reviewer?.displayName || 'Client',
        star_rating: parseInt(review.starRating?.replace('STAR_RATING_', '').replace('_', '') || '5')
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReplyText(response.data.generated_reply);
      toast.success('Réponse IA générée !');
    } catch (error) {
      toast.error('Erreur lors de la génération IA');
    } finally {
      setGeneratingAI(false);
    }
  };

  const submitReply = async (reviewName) => {
    if (!replyText.trim()) {
      toast.error('Veuillez entrer une réponse');
      return;
    }
    setSubmittingReply(true);
    try {
      const reviewId = reviewName.split('/').pop();
      await axios.post(`${BACKEND_URL}/api/google/reviews/${reviewId}/reply?user_id=${user.id}`, {
        comment: replyText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Réponse publiée !');
      setSelectedReview(null);
      setReplyText('');
      loadBusinessData();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    } finally {
      setSubmittingReply(false);
    }
  };

  const getStarRating = (rating) => {
    const num = parseInt(rating?.replace('STAR_RATING_', '').replace('_', '') || '0');
    return num || parseInt(rating) || 0;
  };

  const renderStars = (count) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} 
      />
    ));
  };

  // Not connected state
  if (!connected && !loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white">
        <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold">Google Business</h1>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-green-500 rounded-full mx-auto mb-8 flex items-center justify-center">
            <Building className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Connectez votre fiche Google</h2>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            Gérez votre fiche Google Business directement depuis GESTION GLOBALE : 
            répondez aux avis, publiez des posts, et modifiez vos informations.
          </p>
          <Button onClick={connectGoogle} size="lg" className="bg-blue-600 hover:bg-blue-700">
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5 mr-2" />
            Connecter Google Business
          </Button>
        </main>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Google Business</h1>
              <p className="text-sm text-gray-400">MPHONE</p>
            </div>
          </div>
          <Button onClick={disconnectGoogle} variant="outline" size="sm" className="border-gray-700 text-gray-400">
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-[#12121a] border-b border-gray-800">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'home', label: 'Accueil', icon: Building },
              { id: 'reviews', label: 'Avis', icon: Star },
              { id: 'posts', label: 'Posts', icon: FileText },
              { id: 'info', label: 'Infos', icon: Settings }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id 
                    ? 'border-blue-500 text-blue-500' 
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Business Card */}
            <div className="bg-gradient-to-br from-[#1a1a2e] to-[#12121a] rounded-2xl p-6 border border-gray-800">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-full bg-gray-700 overflow-hidden flex-shrink-0">
                  {businessInfo?.location?.profile?.coverPhoto?.googleUrl ? (
                    <img 
                      src={businessInfo.location.profile.coverPhoto.googleUrl} 
                      alt="Business" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-purple-600">
                      <Building className="w-10 h-10 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold mb-1">
                    {businessInfo?.location?.title || 'MPHONE'}
                  </h2>
                  <p className="text-gray-400 text-sm mb-2">
                    {businessInfo?.location?.storefrontAddress?.addressLines?.join(', ')}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-yellow-400">{reviewsStats.average?.toFixed(1) || '4.8'}</span>
                    <div className="flex">{renderStars(Math.round(reviewsStats.average || 4.8))}</div>
                    <span className="text-gray-400 text-sm">{reviewsStats.total || 0} Avis</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => setActiveTab('reviews')}
                className="bg-[#1a1a2e] rounded-xl p-4 text-left hover:bg-[#252540] transition-colors border border-gray-800"
              >
                <Star className="w-6 h-6 text-yellow-400 mb-2" />
                <p className="text-2xl font-bold">{reviewsStats.unanswered}</p>
                <p className="text-sm text-gray-400">Avis non répondus</p>
              </button>
              
              <button 
                onClick={() => setActiveTab('posts')}
                className="bg-[#1a1a2e] rounded-xl p-4 text-left hover:bg-[#252540] transition-colors border border-gray-800"
              >
                <FileText className="w-6 h-6 text-blue-400 mb-2" />
                <p className="text-2xl font-bold">{posts.length}</p>
                <p className="text-sm text-gray-400">Posts publiés</p>
              </button>
              
              <button 
                onClick={() => setActiveTab('info')}
                className="bg-[#1a1a2e] rounded-xl p-4 text-left hover:bg-[#252540] transition-colors border border-gray-800"
              >
                <Clock className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-sm font-medium">Horaires</p>
                <p className="text-sm text-gray-400">Voir/Modifier</p>
              </button>
              
              <button 
                onClick={() => setActiveTab('info')}
                className="bg-[#1a1a2e] rounded-xl p-4 text-left hover:bg-[#252540] transition-colors border border-gray-800"
              >
                <Image className="w-6 h-6 text-purple-400 mb-2" />
                <p className="text-sm font-medium">Photos</p>
                <p className="text-sm text-gray-400">Gérer</p>
              </button>
            </div>

            {/* Recent Reviews Preview */}
            {reviews.length > 0 && (
              <div className="bg-[#1a1a2e] rounded-xl border border-gray-800">
                <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                  <h3 className="font-semibold">Derniers avis</h3>
                  <Button onClick={() => setActiveTab('reviews')} variant="ghost" size="sm" className="text-blue-400">
                    Voir tous
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="divide-y divide-gray-800">
                  {reviews.slice(0, 3).map((review, idx) => (
                    <div key={idx} className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {review.reviewer?.displayName?.[0] || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{review.reviewer?.displayName || 'Client'}</span>
                          <div className="flex">{renderStars(getStarRating(review.starRating))}</div>
                        </div>
                        <p className="text-sm text-gray-400 truncate">{review.comment || 'Pas de commentaire'}</p>
                      </div>
                      {review.reviewReply ? (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Répondu
                        </span>
                      ) : (
                        <span className="text-xs text-orange-400 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Non répondu
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Avis</h2>
                <p className="text-gray-400">{reviewsStats.total} avis au total</p>
              </div>
              <div className="flex items-center gap-2 bg-[#1a1a2e] px-4 py-2 rounded-lg">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-xl font-bold">{reviewsStats.average?.toFixed(1) || '0'}</span>
              </div>
            </div>

            {/* Reviews List */}
            <div className="space-y-3">
              {reviews.map((review, idx) => (
                <div key={idx} className="bg-[#1a1a2e] rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {review.reviewer?.displayName?.[0] || '?'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold">{review.reviewer?.displayName || 'Client'}</span>
                          <span className="text-xs text-gray-500">
                            {review.createTime ? new Date(review.createTime).toLocaleDateString('fr-FR') : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">{renderStars(getStarRating(review.starRating))}</div>
                          {review.reviewReply ? (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Répondu</span>
                          ) : (
                            <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">Non répondu</span>
                          )}
                        </div>
                        <p className="text-gray-300">{review.comment || 'Pas de commentaire'}</p>
                        
                        {/* Reply Section */}
                        {review.reviewReply && (
                          <div className="mt-3 pl-4 border-l-2 border-blue-500">
                            <p className="text-sm text-gray-400">Votre réponse :</p>
                            <p className="text-sm text-gray-300">{review.reviewReply.comment}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reply Actions */}
                  {!review.reviewReply && (
                    <div className="border-t border-gray-800 p-4 bg-[#12121a]">
                      {selectedReview?.name === review.name ? (
                        <div className="space-y-3">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Écrivez votre réponse..."
                            className="w-full bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 resize-none"
                            rows={3}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => generateAIReply(review)}
                              disabled={generatingAI}
                              variant="outline"
                              size="sm"
                              className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                            >
                              <Sparkles className="w-4 h-4 mr-2" />
                              {generatingAI ? 'Génération...' : 'Générer avec IA'}
                            </Button>
                            <div className="flex-1" />
                            <Button
                              onClick={() => { setSelectedReview(null); setReplyText(''); }}
                              variant="ghost"
                              size="sm"
                              className="text-gray-400"
                            >
                              Annuler
                            </Button>
                            <Button
                              onClick={() => submitReply(review.name)}
                              disabled={submittingReply || !replyText.trim()}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {submittingReply ? 'Publication...' : 'Publier'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setSelectedReview(review)}
                          variant="outline"
                          size="sm"
                          className="border-gray-700 text-gray-300"
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Répondre
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* POSTS TAB */}
        {activeTab === 'posts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Posts</h2>
                <p className="text-gray-400">{posts.length} publications</p>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Post
              </Button>
            </div>

            {posts.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Aucun post</h3>
                <p className="text-gray-400 mb-4">Créez votre premier post pour engager vos clients</p>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer un post
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {posts.map((post, idx) => (
                  <div key={idx} className="bg-[#1a1a2e] rounded-xl border border-gray-800 overflow-hidden">
                    <div className="flex">
                      {post.media?.[0]?.googleUrl && (
                        <img 
                          src={post.media[0].googleUrl} 
                          alt="Post" 
                          className="w-32 h-32 object-cover"
                        />
                      )}
                      <div className="flex-1 p-4">
                        <p className="font-medium mb-2">{post.summary}</p>
                        <p className="text-sm text-gray-500">
                          {post.createTime ? new Date(post.createTime).toLocaleDateString('fr-FR') : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* INFO TAB */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold mb-6">Informations</h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { icon: Image, label: 'Photos de l\'établissement', desc: 'Gérer vos photos' },
                { icon: Calendar, label: 'Horaires exceptionnels', desc: 'Jours fériés, vacances' },
                { icon: Clock, label: 'Horaires d\'ouverture', desc: 'Horaires habituels' },
                { icon: Tag, label: 'Catégories', desc: 'Type d\'établissement' },
                { icon: List, label: 'Services', desc: 'Services proposés' },
                { icon: Search, label: 'Attributs', desc: 'Caractéristiques' },
                { icon: Link2, label: 'Liens', desc: 'Site web, réseaux' },
                { icon: FileText, label: 'Descriptions', desc: 'Texte de présentation' }
              ].map((item, idx) => (
                <button 
                  key={idx}
                  className="bg-[#1a1a2e] rounded-xl p-4 text-left hover:bg-[#252540] transition-colors border border-gray-800 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-[#12121a] border-t border-gray-800 md:hidden">
        <div className="flex justify-around py-2">
          {[
            { id: 'home', label: 'Home', icon: Building },
            { id: 'reviews', label: 'Avis', icon: Star },
            { id: 'posts', label: 'Posts', icon: FileText },
            { id: 'info', label: 'Infos', icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-1 px-4 py-2 ${
                activeTab === tab.id ? 'text-blue-500' : 'text-gray-500'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
